import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  courseLoaded,
  updateCourseSortOrderCompleted, updateCourseSyncClones,
  updateCourseThumbnailSyncClone,
  userPrivateLoaded
} from "../store/course.actions";
import {catchError, concatMap, exhaustMap, filter, first, map, switchMap, take, tap, timeout, withLatestFrom} from 'rxjs/operators';
import {CoursesDBService} from '../services/courses-db.service';
import {combineLatest, EMPTY, of, throwError} from 'rxjs';
import {AppState} from './index';
import {select, Store} from '@ngrx/store';
import {loadDescription} from './description.actions';
import {
  isActiveCourseSectionsLoaded, selectActiveCourse,
  selectPendingCoursesReorder,
  selectPendingSectionsReorder,
  selectUser,
  selectLessonsState
} from './selectors';
import {LessonsDBService} from '../services/lessons-db.service';
import {courseLessonsLoaded} from './lesson.actions';
import {SchoolUsersDbService} from '../services/school-users-db.service';
import {TenantService} from '../services/tenant.service';
import {CourseActions, CourseSectionActions} from './action-types';
import {courseSectionsLoaded, updateSectionOrderCompleted, courseSectionPageLoaded} from './course-section.actions';
import {ProtectedResourceService} from '../services/protected-resource.service';
import {MessagesService} from '../shared/services/messages.service';
import {LoadingService} from '../shared/services/loading.service';
import {loadAllAuthors} from './authors.actions';
import { UserActivityLogService } from "../services/user-activity-log.service";
import { Auth, authState } from '@angular/fire/auth';
import {environment} from "../../environments/environment";
import { StudentsService } from '../admin/manage-students/students.service';
import { CourseDeleteService } from '../services/course-delete.service';


@Injectable()
export class CourseEffects {
  private actions$ = inject(Actions);
  private coursesDB = inject(CoursesDBService);
  private lessonsDB = inject(LessonsDBService);
  private store = inject<Store<AppState>>(Store);
  private loading = inject(LoadingService);
  private messages = inject(MessagesService);
  private afAuth = inject(Auth);
  private usersDB = inject(SchoolUsersDbService);
  private tenant = inject(TenantService);
  private resources = inject(ProtectedResourceService);
  private userActivityLogService = inject(UserActivityLogService);
  private studentsService = inject(StudentsService);
  private courseDeleteService = inject(CourseDeleteService);


  createNewCourse$ = createEffect(() => this.actions$
      .pipe(
        ofType(CourseActions.createNewCourse),
        concatMap(({course}) => this.coursesDB.createNewCourse(course))
      ),
    {dispatch: false});

  loadUserPrivate$ = createEffect(() => combineLatest(
    authState(this.afAuth),
    this.tenant.tenantId$
  )
    .pipe(
      filter(([user, tenantId]) => !!(user?.uid && tenantId)),
      tap(data => console.log("loadUserPrivate$ effect called ...", data)),
      concatMap(([user, tenantId]) => this.usersDB.loadUserPrivate(tenantId, user.uid)),
      map(userPrivate => userPrivateLoaded({...userPrivate})),
      catchError(err => {
        console.log('Could not load user private data on loadUserPrivate$ effect...');
        return throwError(err);
      })
    ));

  reloadUserBundles$ = createEffect(() => this.actions$.pipe(
    ofType(CourseActions.bundlePurchased),
    withLatestFrom(
      this.tenant.tenantId$,
      this.store.pipe(select(selectUser)),
    ),
    tap(console.log),
    concatMap(([bundleId, tenantId, user]) => this.usersDB.loadUserPrivate(tenantId, user.id)),
    map(userPrivate => userPrivateLoaded({...userPrivate})),
    catchError(err => {
      this.messages.error('Could not load user bundles.');
      return throwError(err);
    })
  ));

  loadCourseDescriptionIfNeeded$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.loadCourseDetail),
      map(({courseId}) => loadDescription({descriptionId: courseId})),
      catchError(err => {
        this.messages.error('Could not load course description.');
        return throwError(err);
      })
    ));


  loadFirstSectionsPage$ = createEffect(() => this.actions$.pipe(
    ofType(CourseActions.loadCourseDetail),
    withLatestFrom(this.store.pipe(select(isActiveCourseSectionsLoaded))),
    filter(([action, loaded]) => !action.isWatchCourseRoute && (action.isAdminRoute || !loaded)),
    exhaustMap(([{ courseId, course, isAdminRoute }]) => {
      const targetId = course?.cloneType === 'read-only' && course?.sourceCourseId
        ? course.sourceCourseId : courseId;
      // Admin/edit routes need ALL sections — use full load. Landing page paginates to 3.
      const load$ = isAdminRoute
        ? this.lessonsDB.loadCourseSections(targetId)
        : this.lessonsDB.loadCourseSectionsPage(targetId, 0, 4);
      return this.loading.showLoader(
        load$.pipe(
          map(sections => courseSectionPageLoaded({
            // Admin: store ALL sections. Landing page: store first 3 (4th was lookahead only)
            courseSections: (isAdminRoute ? sections : sections.slice(0, 3)).map(s => ({ ...s, courseId: targetId })),
            courseId,
            allLoaded: isAdminRoute ? true : sections.length < 4,
            direction: 'landing'
          })),
          catchError(err => {
            this.messages.error('Could not load sections.');
            return EMPTY;
          })
        )
      );
    })
  ));

  loadLessonsForNewSections$ = createEffect(() => this.actions$.pipe(
    ofType(CourseSectionActions.courseSectionPageLoaded),
    filter(({ courseSections }) => courseSections.length > 0),
    concatMap(({ courseSections, courseId }) =>
      this.store.pipe(select(selectLessonsState), first()).pipe(
        concatMap(lessonsState => {
          const newSectionIds = courseSections
            .map(s => s.id)
            .filter(id => !lessonsState.loadedSections[id]);
          if (newSectionIds.length === 0) return EMPTY;
          const targetCourseId = courseSections[0]?.courseId ?? courseId;
          return this.lessonsDB.loadLessonsForSections(targetCourseId, newSectionIds).pipe(
            map(lessons => courseLessonsLoaded({ lessons, courseId, sectionIds: newSectionIds })),
            catchError(err => {
              this.messages.error('Could not load lessons.', err?.message ?? String(err));
              return EMPTY;
            })
          );
        })
      )
    )
  ));

  loadMoreSections$ = createEffect(() => this.actions$.pipe(
    ofType(CourseSectionActions.loadCourseSectionsPage, CourseSectionActions.loadCourseSectionsBefore),
    withLatestFrom(this.store.pipe(select(selectActiveCourse))),
    exhaustMap(([action, course]) => {
      const targetId = course?.cloneType === 'read-only' && course?.sourceCourseId
        ? course.sourceCourseId : action.courseId;
      const isNext = action.type === CourseSectionActions.loadCourseSectionsPage.type;
      // Fetch a batch when going next, plus one lookahead: if the lookahead comes back there are
      // more sections, if it does not this was the last page.
      //
      // This used to fetch 2 and keep 1, so every tap of "Load Next Section" cost a full round
      // trip for a single section. A 13-section course therefore needed ten sequential taps, each
      // waiting on its own Firestore query - fine on a fast desktop connection, painfully slow on
      // a phone, which is exactly how it was reported ("takes 30 seconds", "only loads 3-5").
      const nextAction = action as ReturnType<typeof CourseSectionActions.loadCourseSectionsPage>;
      const NEXT_PAGE_SIZE = isNext ? (nextAction.pageSize ?? 1) : 1;
      const load$ = isNext
        ? this.lessonsDB.loadCourseSectionsPage(targetId, nextAction.afterSeqNo, NEXT_PAGE_SIZE + 1)
        : this.lessonsDB.loadCourseSectionsBefore(targetId, (action as ReturnType<typeof CourseSectionActions.loadCourseSectionsBefore>).beforeSeqNo, 1);
      return load$.pipe(
          // A Firestore read on a flaky mobile connection can sit there without resolving or
          // erroring. Because this effect uses exhaustMap, one such request blocks every later
          // one, and the page's button stays disabled behind its spinner - the reported "sections
          // don't load at all". Failing after 20s turns that dead end into a retryable error.
          timeout(20000),
          map(sections => courseSectionPageLoaded({
            // Drop the lookahead - it only existed to tell us whether more sections remain
            courseSections: (isNext ? sections.slice(0, NEXT_PAGE_SIZE) : sections.slice(0, 1)).map(s => ({ ...s, courseId: targetId })),
            courseId: action.courseId,
            allLoaded: isNext ? sections.length < NEXT_PAGE_SIZE + 1 : false,
            direction: isNext ? 'next' : 'prev'
          })),
          catchError(err => {
            this.messages.error('Could not load section.');
            return of(CourseSectionActions.courseSectionPageLoadFailed({ direction: isNext ? 'next' : 'prev' }));
          })
      );
    })
  ));

  loadActiveCourseSection$ = createEffect(() => this.actions$.pipe(
    ofType(CourseActions.loadActiveCourseSection),
    withLatestFrom(this.store.pipe(select(selectActiveCourse))),
    switchMap(([{ courseId, sectionSeqNo }, course]) => {
      const targetId = course?.cloneType === 'read-only' && course?.sourceCourseId
        ? course.sourceCourseId : courseId;
      // Fetch the active section PLUS one lookahead (seqNo > sectionSeqNo - 1, limit 2)
      // in a single query. If only the active section comes back, there is no next
      // section → mark allLoaded so the "Load next section" button is correctly
      // disabled (e.g. single-section courses, or refreshing while on the last section).
      return this.lessonsDB.loadCourseSectionsPage(targetId, sectionSeqNo - 1, 2).pipe(
        map(sections => courseSectionPageLoaded({
          // Keep only the active section — the 2nd was a lookahead to detect more.
          courseSections: sections.slice(0, 1).map(s => ({ ...s, courseId: targetId })),
          courseId,
          allLoaded: sections.length < 2,
          direction: 'initial'
        })),
        catchError(err => {
          this.messages.error('Could not load section.');
          return EMPTY;
        })
      );
    })
  ));

  loadAuthorsIfNeeded$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.loadCourseDetail),
      map(() => loadAllAuthors()),
      catchError(err => {
        this.messages.error('Could not load authors.');
        return throwError(err);
      })
    ));

  deleteCourse$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.deleteCourse),
      concatMap(({ id }) =>
        this.coursesDB.findCourseByTenantAndId(this.tenant.id, id).pipe(
          first(),
          switchMap((course) => {
            if (course?.type === 'scorm') {
              return this.loading.showLoader(this.courseDeleteService.deleteScormCourseWithCleanup(id));
            } else {
              return this.loading.showLoader(this.courseDeleteService.deleteCourseWithCleanup(id));
            }
          })
        )
      ),
      catchError(err => {
        this.messages.error('Could not delete the course.', err);
        return throwError(err);
      })
    ), { dispatch: false });


  saveCourse$ = createEffect(() => this.actions$
      .pipe(
        ofType(CourseActions.updateCourse),
        withLatestFrom(
          this.store.pipe(select(selectActiveCourse)),
        ),
        concatMap(([{course}, activeCourse]) => {

          const obs$ =  this.loading.showLoader(this.coursesDB.saveCourse(course.id, course.changes))

          if(environment?.features?.cloneCourse) {

            this.store.dispatch(updateCourseSyncClones({course}))

            if(course?.changes?.thumbnail && course?.changes?.thumbnail?.trim()?.length > 0) {
              this.store.dispatch(updateCourseThumbnailSyncClone({course}))
            }

          }

          return obs$.pipe(
            catchError(err => {
              this.messages.error('Could not save course.');
              return EMPTY;
            })
          );

        })
      ),
    {dispatch: false});


  saveCoursesReordering$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.updateCourseSortOrder),
      withLatestFrom(this.store.pipe(select(selectPendingCoursesReorder))),
      concatMap(([action, changes]) => this.coursesDB.updateCourses(changes)),
      map(() => updateCourseSortOrderCompleted()),
      catchError(err => {
        this.messages.error('Could not save the new course order.');
        return throwError(err);
      })
    ));

  saveCourseSectionReordering$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseSectionActions.updateSectionOrder),
      withLatestFrom(this.store.pipe(select(selectPendingSectionsReorder))),
      concatMap(([{courseId}, changes]) => this.coursesDB.updateCourseSections(courseId, changes)),
      map(() => updateSectionOrderCompleted()),
      catchError(err => {
        this.messages.error('Could not save the new section order.');
        return throwError(err);
      })
    ));

  publishCourse$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.coursePublished),
      concatMap(action => this.coursesDB.saveCourse(action.courseId, {status: 'published', url: action.url})),
      catchError(err => {
        this.messages.error('Error publishing course.');
        return throwError(err);
      })
    ), {dispatch: false});

  unpublishCourse$ = createEffect(() => this.actions$
      .pipe(
        ofType(CourseActions.courseUnpublished),
        concatMap(action => this.coursesDB.saveCourse(action.courseId, {status: 'draft'})),
        catchError(err => {
          this.messages.error('Error unpublishing course.');
          return throwError(err);
        })
      ),
    {dispatch: false});

  reportCourseEnrollmentWhenCoursePurchased$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.coursePurchased),
      withLatestFrom(this.store.pipe(select(selectUser))),
      concatMap(async ([action, user]) => {
          console.log(`Reporting COURSE PURCHASED for user ${user.id} and course ${action.courseId}`);
        // await this.studentsService.createStudentEnrollment(user.id, action.courseId);
        return await this.userActivityLogService.reportCourseEnrollment(user.id,action.courseId);
      }),
      catchError(err => {
        this.messages.error('Error purchasing course.');
        return throwError(err);

      } )
    ), {dispatch: false});

  loadSourceReadOnlyCloneCourse$ = createEffect(() => this.actions$
    .pipe(
      ofType(CourseActions.courseLoaded),
      concatMap(action => {
        if (action.course?.cloneType === 'read-only') {
          return this.coursesDB.findCourseByTenantAndId(this.tenant.id, action.course.sourceCourseId).pipe(take(1))
        }
        return EMPTY
      }),
      tap((course) => {
        if (course) {
          this.store.dispatch(courseLoaded({course}))
        }
      }),
      catchError(err => {
        this.messages.error('Error loading original source course.');
        return throwError(() => err);
      }),
    ), { dispatch: false });

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() {

  }


}
